import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params, Router, RouterStateSnapshot } from '@angular/router';
import { Customer, FeatureFlags } from '@inyova/models';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { fetchCustomerSuccess } from '@app/app.actions';
import { logout } from '@profile/profile.actions';
import { updateStrategyEditMode } from '@strategy/strategy.actions';

import { RegisterData } from '@shared/models/Auth';
import { TokenService } from '@shared/services/token.service';
import { TrackingService } from '@shared/services/tracking.service';
import { anonymiseRedirectURL, getCookie, publicURLs, setCookie } from '@shared/utils';
import { environment } from 'src/environments/environment';

import { SharedService } from './shared.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  //
  isLoggedIn = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.isLoggedIn.asObservable();

  isAdmin: string;
  redirect: string;
  editModeStatus: string;
  selectedAccount: string;

  constructor(
    private router: Router,
    private store: Store,
    private trackingService: TrackingService,
    private tokenService: TokenService,
    private sharedService: SharedService,
    private http: HttpClient
  ) {}

  checkSession(routerState: RouterStateSnapshot, params: Params): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      // Check token and if it's not there redirects login
      if (!getCookie('accessToken')) {
        const queryParams = { redirect: anonymiseRedirectURL(routerState.url.split('?')[0]), ...params };
        void this.router.navigate(['/login'], { queryParams });
        observer.next(false);
        observer.complete();
        return;
      }

      this.tokenService.validateToken().subscribe(
        async ({ status, data }) => {
          const currentPath = routerState.url.split('?')[0];

          if (status !== 'success') {
            observer.next(false);
            observer.complete();
            return;
          }

          this.store.dispatch(fetchCustomerSuccess({ customer: data, selectActiveAccount: data.temporary_accounts.length > 0 }));
          this.updateLoggedIn(true);
          this.trackingService.trackLastTouchPoint();

          if (!data.email_confirmed) {
            if (currentPath === '/next/verify-email') {
              observer.next(true);
              observer.complete();
              return;
            }

            void this.router.navigate(['/next/verify-email']);
            observer.next(false);
            observer.complete();
            return;
          }

          if (currentPath === '/next/verify-email') {
            void this.router.navigate(['/']);
            observer.next(false);
            observer.complete();
            return;
          }

          if (!data.temporary_accounts.length && !data.has_crowd_investor_account) {
            void this.router.navigate(['/']);
            observer.next(false);
            observer.complete();
            return;
          }

          observer.next(true);
          observer.complete();
        },
        // ERROR
        () => {
          this.logout(true, false);
          observer.next(false);
          observer.complete();
        }
      );
    });
  }

  signIn({ email, password }: { email: string; password: string }) {
    return this.http
      .post<{ body: { status: string; data: Customer }; headers: HttpHeaders }>(
        `${environment.apiUrl}/auth/sign_in`,
        {
          email,
          password
        },
        { observe: 'response' as 'body' }
      )
      .pipe(
        tap(({ headers, body }) => {
          this.tokenService.setTokenData(headers, body.status === 'success' && !body.data ? '2fa' : 'headers');
        })
      );
  }

  loginWithGoogle(token: string): Observable<{ status: string; data: Customer }> {
    const body = { token };

    return this.http.post(`${environment.apiUrl}/google_sessions`, body, { headers: { skip: 'true' } }).pipe(
      tap((response) => {
        this.tokenService.setTokenData(response, 'object');
      }),
      switchMap(() => this.tokenService.validateToken())
    );
  }

  logout(autoLogout?: boolean, clearParams = false): void {
    // For mobile embedded iframe: send postMessage event to parent > mobile iframe and manage logout on mobile
    parent.postMessage('logout', '*');

    // if there is no token direct manage logout
    if (!this.tokenService.tokenData.value) {
      this.handleLogout(autoLogout, clearParams);
      return;
    }

    // if there is token but not valid call sign_out
    this.http.delete<any>(`${environment.apiUrl}/auth/sign_out`).subscribe(
      () => this.handleLogout(autoLogout, clearParams),
      () => this.handleLogout(autoLogout, clearParams)
    );
  }

  updatePassword(body: {
    password: string;
    password_confirmation: string;
    reset_password_token: string;
  }): Observable<{ data: Customer; status: boolean }> {
    return this.http.put<{ data: Customer; status: boolean }>(`${environment.apiUrl}/auth/password`, body);
  }

  registerAccount(registerData: RegisterData): Observable<{ body: { status: string; data: Customer }; headers: HttpHeaders }> {
    return this.http
      .post<{
        body: { status: string; data: Customer };
        headers: HttpHeaders;
      }>(`${environment.apiUrl}/auth`, { ...registerData }, { observe: 'response' as 'body' })
      .pipe(
        tap(({ headers }) => {
          this.tokenService.setTokenData(headers);
        })
      );
  }

  // Reset password request
  resetPassword({ email }: { email: string }): Observable<{
    message: string;
    success: boolean;
  }> {
    return this.http.post<{
      message: string;
      success: boolean;
    }>(`${environment.apiUrl}/auth/password`, { email });
  }

  updateLoggedIn(_bool: boolean): void {
    this.isLoggedIn.next(_bool);
  }

  validate2FACode(code, token): Observable<{ body: { status: string; data: Customer }; headers: HttpHeaders }> {
    const req = this.http
      .post<{
        body: { data: Customer; status: string };
        headers: HttpHeaders;
      }>(
        `${environment.apiUrl}/multi_factor_auth/verify_2fa_token/?otp_code_token=${code}&authentication_token=${token}`,
        {},
        { observe: 'response' as 'body', headers: { skip: 'true' } }
      )
      .pipe(
        tap(({ headers }) => {
          this.tokenService.setTokenData(headers);
        })
      );

    return req;
  }

  autoLogin(queryParams): Observable<boolean> {
    const { selected_account, customer_token, is_admin, edit, sti, redirect } = queryParams;

    // Sign in with pre-selected account
    this.selectedAccount = selected_account ?? '';
    // Redirect after successful auth token validation
    this.redirect = redirect ?? '';
    // Check if edit mode should be active
    this.editModeStatus = edit ?? '';
    // Get sti from URL

    // Save the lastest STI for the customer in cookies
    if (sti) {
      setCookie('sti', sti as string, 30);
    }

    return new Observable<boolean>((observer) => {
      this.http
        .get<{ body: { status: string; data: Customer }; headers: HttpHeaders }>(
          `${environment.apiUrl}/validate-yova-token/${customer_token}${is_admin ? '?is_admin=1' : ''}`,
          {
            observe: 'response' as 'body'
          }
        )
        .pipe(
          tap(({ headers }) => {
            this.tokenService.setTokenData(headers);
          })
        )
        .subscribe(
          ({ body }) => {
            if (body && body.status === 'success') {
              window.localStorage.clear();

              if (this.redirect.includes('new-account') && !this.selectedAccount) {
                this.selectedAccount = body.data.temporary_accounts.find((account) => account.kind === '3a')?.id.toString() || '';
              }

              if (this.selectedAccount && !localStorage.getItem('currentAccountID')) {
                localStorage.setItem('currentAccountID', this.selectedAccount);
              }

              if (this.redirect) {
                this.sharedService.expireMobilePromo(1);
                if (body.data.app_location === 'ch' && body.data.temporary_accounts && body.data.temporary_accounts.length === 0) {
                  void this.router.navigate(['/products']);
                  return;
                }
                void this.router.navigate(this.redirect.split(','));
              } else {
                void this.router.navigate(['/']);
              }

              this.store.dispatch(fetchCustomerSuccess({ customer: body.data, selectActiveAccount: !!body.data.temporary_accounts.length }));

              this.updateLoggedIn(true);

              if (this.selectedAccount && this.editModeStatus === 'active') {
                const account = body.data.temporary_accounts.find((account) => account.id === this.selectedAccount);
                if (account.enabled_features.includes(FeatureFlags.EVE2)) {
                  return;
                }
                this.store.dispatch(updateStrategyEditMode({ editMode: true }));
              }
            }
          },
          // ERROR
          () => {
            this.logout(true, false);
            observer.next(false);
          }
        );
    });
  }

  private handleLogout(autoLogout = false, clearParams = false) {
    this.tokenService.setTokenData(null);
    this.store.dispatch(logout());
    this.updateLoggedIn(false);

    const url = `${window.location.pathname + window.location.search}`;

    // If customer is already on any public route don't redirect
    if (publicURLs.includes(url)) return;

    let queryParams: { autoLogout?: boolean; redirect?: string } = { redirect: anonymiseRedirectURL(url) };

    if (autoLogout) {
      queryParams = {
        ...queryParams,
        autoLogout: true
      };
    }

    if (url === '/') {
      delete queryParams.redirect;
    }

    void this.router.navigate(['/login'], { queryParams: clearParams ? {} : queryParams });
  }
}
